import { isNative } from "../lib/platform.js";
import {
  loadTokens,
  saveTokens,
  clearTokens as clearTokenStore,
} from "../lib/tokenStore.js";

const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

let _onUnauthorized = null;

/** Register a callback to invoke when the server returns 401 (session expired). */
export function setUnauthorizedHandler(fn) {
  _onUnauthorized = fn;
}

let _onBillingSuspended = null;

/** Register a callback to invoke when the server returns 402 BILLING_SUSPENDED. */
export function setBillingSuspendedHandler(fn) {
  _onBillingSuspended = fn;
}

// In-memory access token — survives React re-renders, cleared on page reload.
// Fallback for browsers that block cross-site httpOnly cookies (iOS Safari ITP,
// incognito mode). The httpOnly cookie path still works where allowed.
let _accessToken = null;
export function setAccessToken(t) {
  _accessToken = t;
}
/** Read-only accessor — used by WebSocketContext to auth the WS upgrade. */
export function getAccessToken() {
  return _accessToken;
}

/**
 * En nativo (Capacitor), carga el access token persistido a `_accessToken` para
 * que la primera petición autenticada tras arrancar la app ya lleve el header
 * Bearer. Devuelve true si restauró alguno. En web es no-op y devuelve false.
 */
export async function hydrateTokensFromStore() {
  if (!isNative()) return false;
  const { accessToken } = await loadTokens();
  if (accessToken) {
    _accessToken = accessToken;
    return true;
  }
  return false;
}

/** Borra los tokens persistidos del almacenamiento nativo (logout). Web → no-op. */
export async function clearPersistedTokens() {
  await clearTokenStore();
}

// Single-flight refresh: _refreshing stays set for the full duration of the
// request (including JSON parse) so that concurrent 401s all await the same
// promise and never trigger a second /refresh call.
let _refreshing = null;

/**
 * Attempt a silent token refresh.  Called automatically on 401s inside
 * `request()` and also exported so AuthContext can call it proactively on
 * app boot — ensuring _accessToken is populated on page reload before the
 * first authenticated request, fixing the ITP/incognito reload regression.
 *
 * Returns true if a new accessToken was obtained, false otherwise.
 */
export async function tryRefresh() {
  if (_refreshing) return _refreshing;
  _refreshing = (async () => {
    try {
      // El cliente nativo (Capacitor) no puede apoyarse en cookies httpOnly:
      // manda el refresh token por header y recibe el par nuevo en el cuerpo.
      const opts = { method: "POST", credentials: "include" };
      if (isNative()) {
        const { refreshToken } = await loadTokens();
        opts.headers = {
          "X-Client": "mobile",
          ...(refreshToken ? { "X-Refresh-Token": refreshToken } : {}),
        };
      }
      const r = await fetch(`${BASE_URL}/api/auth/refresh`, opts);
      if (!r.ok) {
        _accessToken = null; // refresh rejected — don't keep sending a stale token
        return false;
      }
      const body = await r.json();
      if (body.accessToken) _accessToken = body.accessToken;
      // Persistir el par rotado en nativo. Van los DOS tokens: saveTokens ignora
      // los valores falsy, así que omitir refreshToken dejaría el VIEJO guardado
      // y el siguiente refresh usaría un token ya rotado.
      if (isNative()) {
        await saveTokens({
          accessToken: body.accessToken,
          refreshToken: body.refreshToken,
        });
      }
      return true;
    } catch {
      _accessToken = null; // fail safe — don't keep sending a token we couldn't refresh
      return false;
    } finally {
      _refreshing = null; // cleared only after full completion (including JSON parse)
    }
  })();
  return _refreshing;
}

// Default per-request timeout. Endpoints with heavier payloads (batch fetches,
// reports) can override via `options.timeoutMs`.
const DEFAULT_TIMEOUT_MS = 10_000;

/**
 * Codifica un valor que va a parar a un SEGMENTO de la ruta.
 *
 * Interpolado crudo, un valor con `/`, `?` o `#` deja de ser un segmento y
 * pasa a cambiar la petición: `a/b` aterriza en otro endpoint, un `?` abre el
 * query a mitad de camino y todo lo que sigue se descarta como ruta. El imei
 * es el caso vivo —entra por el query param `?imei=` de Comandos, que cualquiera
 * puede editar en la barra de direcciones, y también sale de registros viejos—,
 * pero la regla vale para cualquier identificador que venga de afuera.
 *
 * No normaliza ni valida: eso es del backend. Solo garantiza que lo que se
 * pidió como UN segmento viaje como UN segmento.
 */
const seg = (value) => encodeURIComponent(value);

async function request(path, options = {}, _retry = true) {
  const {
    timeoutMs: rawTimeoutMs,
    signal: externalSignal,
    ...fetchOptions
  } = options;
  // Guard against invalid values (NaN, 0, negative, non-number). Fall back to
  // the default so a typo at the call site can't silently break aborts.
  const timeoutMs =
    Number.isFinite(rawTimeoutMs) && rawTimeoutMs > 0
      ? rawTimeoutMs
      : DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    // Abort with an explicit reason so consumers see a meaningful error name
    // ("TimeoutError") and message instead of "signal is aborted without reason".
    const reason =
      typeof DOMException !== "undefined"
        ? new DOMException(
            `Request timeout after ${timeoutMs}ms (${path})`,
            "TimeoutError",
          )
        : Object.assign(new Error(`Request timeout after ${timeoutMs}ms`), {
            name: "TimeoutError",
          });
    controller.abort(reason);
  }, timeoutMs);

  // If an external abort signal is provided (e.g. from a component unmount
  // controller), wire it to the internal timeout controller so either source
  // can cancel the fetch.
  let onExternalAbort = null;
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      controller.abort(externalSignal.reason);
    } else {
      onExternalAbort = () => controller.abort(externalSignal.reason);
      externalSignal.addEventListener("abort", onExternalAbort, { once: true });
    }
  }

  const authHeader = _accessToken
    ? { Authorization: `Bearer ${_accessToken}` }
    : {};

  // FormData bodies (multipart uploads) must NOT carry an explicit
  // Content-Type — the browser sets it along with the multipart boundary.
  // Setting it manually strips the boundary and breaks the upload.
  const isForm =
    typeof FormData !== "undefined" && fetchOptions.body instanceof FormData;
  // Pull caller headers out so the `...restFetchOptions` spread below can't
  // clobber the merged header object (Content-Type / Authorization). Callers
  // that pass `headers: {}` (e.g. FormData uploads) still get the auth header.
  const { headers: callerHeaders, ...restFetchOptions } = fetchOptions;

  // El cliente nativo marca cada petición para que el backend le sirva el
  // contrato móvil (Bearer + refresh por cuerpo) en vez del de cookies.
  const clientHeader = isNative() ? { "X-Client": "mobile" } : {};

  try {
    let res;
    try {
      res = await fetch(`${BASE_URL}${path}`, {
        credentials: "include", // send httpOnly cookies where allowed
        ...restFetchOptions,
        headers: {
          ...(isForm ? {} : { "Content-Type": "application/json" }),
          ...authHeader,
          ...clientHeader,
          ...callerHeaders,
        },
        signal: controller.signal,
      });
    } catch (netErr) {
      // fetch rechaza con TypeError("Failed to fetch") ante CUALQUIER fallo de
      // red: sin conexión, DNS caído, servidor no responde, preflight CORS
      // rechazado. Ese texto en inglés llegaba tal cual a la UI. Lo traducimos
      // y lo marcamos con `code: "NETWORK"` para que quien quiera ofrecer un
      // camino alternativo (p. ej. guardar sin foto) lo distinga de un 4xx/5xx.
      // Los abortos (TimeoutError / AbortError) se re-lanzan intactos: hay
      // consumidores que discriminan por `err.name`.
      if (netErr instanceof TypeError) {
        const err = new Error(
          "No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.",
        );
        err.status = 0;
        err.code = "NETWORK";
        err.cause = netErr;
        throw err;
      }
      throw netErr;
    }

    if (res.status === 401 && _retry) {
      // Skip refresh only for login-flow endpoints — 401 there means bad credentials.
      // /api/auth/me, /api/auth/logout, /api/auth/request-password-change etc.
      // are authenticated endpoints and SHOULD trigger a refresh on expiry.
      const skipRefresh = [
        "/api/auth/login",
        "/api/auth/verify-2fa",
        "/api/auth/refresh",
        "/api/auth/forgot-password",
        "/api/auth/verify-forgot-otp",
        "/api/auth/reset-password",
      ].some((ep) => path === ep || path.startsWith(ep + "?"));

      if (!skipRefresh) {
        const refreshed = await tryRefresh();
        if (refreshed) return request(path, options, false);
        if (_onUnauthorized) _onUnauthorized();
      }
      const err = new Error(
        skipRefresh ? "Credenciales inválidas" : "Sesión expirada",
      );
      err.status = 401;
      throw err;
    }

    if (res.status === 402) {
      const body = await res.json().catch(() => ({}));
      if (body.code === "BILLING_SUSPENDED" && _onBillingSuspended) {
        _onBillingSuspended(body);
      }
      const err = new Error(
        body.error || "Cuenta suspendida por mora (BILLING_SUSPENDED)",
      );
      err.status = 402;
      throw err;
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || `HTTP ${res.status}`);
      err.status = res.status;
      // El `code` del backend viaja con el error: distinguir un conflicto
      // concreto (p. ej. BYPASS_ALREADY_ACTIVE) de un 409 cualquiera por el
      // texto del mensaje obliga a la UI a parsear inglés. Mismo criterio que
      // el `code: "NETWORK"` de más arriba.
      if (body.code) err.code = body.code;
      throw err;
    }

    if (options.raw) return res;
    return res.status === 204 ? null : res.json();
  } finally {
    clearTimeout(timeoutId);
    // Remove the external-abort listener on normal completion so it can't
    // accumulate on a long-lived external signal reused across requests.
    if (onExternalAbort) {
      externalSignal.removeEventListener("abort", onExternalAbort);
    }
  }
}

export const api = {
  // Auth
  login: (body) =>
    request("/api/auth/login", { method: "POST", body: JSON.stringify(body) }),
  // DEV-ONLY: emite cookies para el master sin password. Backend devuelve 404
  // en producción (NODE_ENV=production), así que llamar acá fuera de dev es no-op.
  devLogin: (body = {}) =>
    request("/api/auth/dev-login", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  verifyMfa: (body) =>
    request("/api/auth/verify-2fa", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  logout: () => request("/api/auth/logout", { method: "POST" }),
  getMe: () => request("/api/auth/me"),
  getMeContext: () => request("/api/me/context"),
  updateMe: (body) =>
    request("/api/auth/me", { method: "PUT", body: JSON.stringify(body) }),

  // MFA management (authenticated user)
  enableMfa: () => request("/api/auth/mfa/enable", { method: "POST" }),
  confirmMfa: (code) =>
    request("/api/auth/mfa/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  disableMfa: (password) =>
    request("/api/auth/mfa/disable", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  // Password change (authenticated user)
  requestPasswordChange: () =>
    request("/api/auth/request-password-change", { method: "POST" }),
  verifyChangeOtp: (code) =>
    request("/api/auth/verify-change-otp", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  changePassword: (code, password) =>
    request("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ code, password }),
    }),

  // Password reset (unauthenticated — forgot password flow)
  forgotPassword: (email) =>
    request("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  verifyForgotOtp: (email, code) =>
    request("/api/auth/verify-forgot-otp", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    }),
  resetPassword: (email, code, password) =>
    request("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ email, code, password }),
    }),

  // Users (master only in PR-A baseline — backend gates by role)
  // Optional `params` object is serialized as a query string (e.g. { role: 'viewer' }).
  // Existing call sites that pass no args keep working.
  getUsers: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return request(`/api/users${qs ? `?${qs}` : ""}`);
  },
  createUser: (body) =>
    request("/api/users", { method: "POST", body: JSON.stringify(body) }),
  updateUser: (id, body) =>
    request(`/api/users/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteUser: (id) => request(`/api/users/${id}`, { method: "DELETE" }),

  // Tenants (master only)
  getTenants: () => request("/api/tenants"),
  getTenant: (id) => request(`/api/tenants/${id}`),
  createTenant: (body) =>
    request("/api/tenants", { method: "POST", body: JSON.stringify(body) }),
  updateTenant: (id, body) =>
    request(`/api/tenants/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteTenant: (id) => request(`/api/tenants/${id}`, { method: "DELETE" }),

  // Viewer device assignments (master/operator)
  getUserDeviceAssignments: (userId) =>
    request(`/api/users/${userId}/device-assignments`),
  assignDeviceToUser: (userId, body) =>
    request(`/api/users/${userId}/device-assignments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  unassignDeviceFromUser: (userId, imei) =>
    request(
      `/api/users/${userId}/device-assignments/${encodeURIComponent(imei)}`,
      { method: "DELETE" },
    ),

  getDevices({ signal, since, timeoutMs } = {}) {
    // `since` (ISO 8601) pide un delta: solo devices con updated_at posterior.
    // Sin `since` la respuesta es la flota completa (contrato histórico).
    const qs = since ? `?${new URLSearchParams({ since })}` : "";
    return request(`/api/devices${qs}`, {
      ...(signal ? { signal } : {}),
      ...(timeoutMs ? { timeoutMs } : {}),
    });
  },

  getPositions(imei, { limit = 500, since = null } = {}) {
    const params = new URLSearchParams({ limit });
    if (since) params.set("since", since);
    return request(`/api/devices/${seg(imei)}/positions?${params}`);
  },

  getPositionsBatch(imeis, limit = 30, { signal } = {}) {
    const params = new URLSearchParams({ imeis: imeis.join(","), limit });
    // Batch endpoint scales with N devices × limit positions. Cold-start backends
    // (Fly.io) and fleets >20 devices regularly exceed the default 10s timeout
    // → bump to 30s. See useDevices.initTraces — it tolerates failure but the
    // user-visible trail rendering is delayed until WS catches up.
    return request(`/api/positions/batch?${params}`, {
      timeoutMs: 30_000,
      ...(signal ? { signal } : {}),
    });
  },

  /**
   * Igual que `getPositionsBatch` pero con los IMEIs en el body.
   *
   * Es el camino para flotas grandes: la variante GET mete los IMEIs en la
   * query string (~16 KB con 1.000 vehículos, por encima de lo que aceptan
   * proxies y nginx) y el backend la corta en 50 IMEIs por request. El POST
   * acepta hasta 500. El troceo y la concurrencia los pone
   * `lib/fleet/positionsBatch.js` — acá solo va el transporte.
   */
  postPositionsBatch(imeis, limit = 30, { signal } = {}) {
    return request(`/api/positions/batch`, {
      method: "POST",
      body: JSON.stringify({ imeis, limit }),
      timeoutMs: 30_000,
      ...(signal ? { signal } : {}),
    });
  },

  sendRelayCommand(imei, type) {
    return request(`/api/devices/${seg(imei)}/relay`, {
      method: "POST",
      body: JSON.stringify({ type }),
    });
  },

  // Salida segura — autoriza N salidas de geocerca sin corte de combustible.
  // No manda nada al equipo: es estado de la plataforma, así que funciona con
  // el vehículo apagado (que es cuando se pide).
  getExitBypass(imei) {
    return request(`/api/devices/${seg(imei)}/exit-bypass`);
  },
  createExitBypass(imei, body) {
    return request(`/api/devices/${seg(imei)}/exit-bypass`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  revokeExitBypass(imei) {
    return request(`/api/devices/${seg(imei)}/exit-bypass`, { method: "DELETE" });
  },

  // Inspections / Revisiones
  getPendingInspections() {
    return request(`/api/inspections/pending`);
  },
  getFleetHealth() {
    return request(`/api/inspections/fleet-health`);
  },
  getVehicleInspections(vehicleId) {
    return request(`/api/inspections/vehicle/${vehicleId}`);
  },
  getInspection(id) {
    return request(`/api/inspections/${id}`);
  },
  createInspection(body) {
    return request(`/api/inspections`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Entrega y recibo de vehículos. Cada punta guarda una revisión completa
  // (mismo checklist que arriba) y el backend la persiste junto con la
  // asignación del conductor en UNA transacción — ver api/handoversRoutes.js.
  getVehicleHandovers(vehicleId) {
    return request(`/api/handovers/vehicle/${encodeURIComponent(vehicleId)}`);
  },
  // Conductores elegibles para ESTE vehículo (acotados a su cliente). No es
  // `getDrivers`: esa, para el master, lista los de todos los clientes.
  getHandoverDrivers(vehicleId) {
    return request(`/api/handovers/vehicle/${encodeURIComponent(vehicleId)}/drivers`);
  },
  // La plata del préstamo: contrato vivo con su base y su deuda, la base que se
  // cobrará en la próxima entrega y las cajas de la unidad. El panel la necesita
  // para mostrar la liquidación ANTES de guardarla.
  getHandoverMoney(vehicleId) {
    return request(`/api/handovers/vehicle/${encodeURIComponent(vehicleId)}/money`);
  },
  deliverVehicle(body) {
    return request(`/api/handovers`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  returnVehicle(handoverId, body) {
    return request(`/api/handovers/${encodeURIComponent(handoverId)}/return`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  // Recibir por la moto, para las que salieron SIN entrega registrada en la app
  // (contratos importados del Excel, asignaciones hechas desde Flota). Si
  // resulta que sí tenía una abierta, el backend recibe esa.
  returnVehicleWithoutHandover(vehicleId, body) {
    return request(`/api/handovers/vehicle/${encodeURIComponent(vehicleId)}/return`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  getActiveSessions() {
    return request(`/api/inspections/sessions?status=active`);
  },
  getSessionHistory({ limit = 20, offset = 0, q = "", kind = "", from = "", to = "" } = {}) {
    const qs = new URLSearchParams({ limit, offset });
    if (q) qs.set("q", q);
    // Recortes de la tabla: los resuelve el servidor, como la búsqueda.
    if (kind) qs.set("kind", kind);
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    return request(`/api/inspections/sessions/history?${qs}`);
  },
  getSession(id) {
    return request(`/api/inspections/sessions/${id}`);
  },
  createSession(body) {
    return request(`/api/inspections/sessions`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  closeSession(id) {
    return request(`/api/inspections/sessions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "closed" }),
    });
  },
  // Exclusiones de la revisión semanal (migración 122). No caducan: se levantan
  // con `includeVehicleInInspections`.
  getInspectionExclusions() {
    return request(`/api/inspections/exclusions`);
  },
  excludeVehicleFromInspections(vehicleId, reason) {
    return request(`/api/inspections/exclusions/${vehicleId}`, {
      method: "PUT",
      body: JSON.stringify({ reason: reason ?? null }),
    });
  },
  includeVehicleInInspections(vehicleId) {
    return request(`/api/inspections/exclusions/${vehicleId}`, {
      method: "DELETE",
    });
  },
  // Saltar un vehículo en ESTA sesión, sin excluirlo de las próximas: borra el
  // miembro y el rollover del lunes lo devuelve solo. Distinto de
  // `excludeVehicleFromInspections`, que no caduca.
  skipVehicleInSession(sessionId, vehicleId) {
    return request(
      `/api/inspections/sessions/${sessionId}/members/${vehicleId}`,
      { method: "DELETE" },
    );
  },
  // Deshacer el salto. Saltar es la acción reversible del par —excluir es la
  // que no caduca— así que tiene que tener reverso: el ⋮ está pegado a la
  // placa y un toque de más dejaba la moto fuera hasta el lunes.
  restoreVehicleInSession(sessionId, vehicleId) {
    return request(
      `/api/inspections/sessions/${sessionId}/members/${vehicleId}/restore`,
      { method: "POST" },
    );
  },

  getCommands(imei, limit = 20) {
    return request(`/api/devices/${seg(imei)}/commands?limit=${limit}`);
  },

  sendCommand(imei, type, payload = null) {
    return request(`/api/devices/${seg(imei)}/command`, {
      method: "POST",
      body: JSON.stringify({ type, payload }),
    });
  },

  /**
   * Un mismo comando para varios equipos en un solo request.
   *
   * Responde 200 aunque parte del lote no salga: el detalle por equipo va en
   * `results` y el conteo en `summary`. Los desconectados se reportan como
   * fallidos, no revientan el envío del resto.
   *
   * @param {string[]} imeis
   * @param {'LOCK'|'UNLOCK'|'STATUS'|'CUSTOM'} type
   * @param {string|null} [payload]  requerido para CUSTOM
   * @returns {Promise<{ results: Array, summary: { total, sent, failed } }>}
   */
  sendBulkCommand(imeis, type, payload = null) {
    return request("/api/devices/commands/bulk", {
      method: "POST",
      body: JSON.stringify({ imeis, type, payload }),
      timeoutMs: 60_000,
    });
  },

  // Fleet
  getFleetStatus: () => request("/api/fleet/status"),
  getVehicles: () => request("/api/fleet/vehicles"),
  createVehicle: (body) =>
    request("/api/fleet/vehicles", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateVehicle: (id, body) =>
    request(`/api/fleet/vehicles/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteVehicle: (id) =>
    request(`/api/fleet/vehicles/${id}`, { method: "DELETE" }),
  assignDriver: (vehicleId, body) =>
    request(`/api/fleet/vehicles/${vehicleId}/assign-driver`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  unassignDriver: (vehicleId) =>
    request(`/api/fleet/vehicles/${vehicleId}/assign-driver`, {
      method: "DELETE",
    }),
  assignGps: (vehicleId, body) =>
    request(`/api/fleet/vehicles/${vehicleId}/assign-gps`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // `previous_fate` ('stock' | 'damaged' | 'lost') dice qué pasa con el equipo
  // que el vehículo deja: dañado y perdido lo retiran del mapa y del Monitor.
  // En el DELETE viaja en la query string (un DELETE con body no siempre llega).
  unassignGps: (vehicleId, { previous_fate } = {}) =>
    request(
      `/api/fleet/vehicles/${vehicleId}/assign-gps${
        previous_fate ? `?${new URLSearchParams({ previous_fate })}` : ""
      }`,
      { method: "DELETE" },
    ),
  getDrivers: () => request("/api/fleet/drivers"),
  getDriver: (id) => request(`/api/fleet/drivers/${id}`),
  createDriver: (body) =>
    request("/api/fleet/drivers", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateDriver: (id, body) =>
    request(`/api/fleet/drivers/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteDriver: (id) =>
    request(`/api/fleet/drivers/${id}`, { method: "DELETE" }),
  getGpsDevices: () => request("/api/fleet/gps-devices"),
  createGpsDevice: (body) =>
    request("/api/fleet/gps-devices", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateGpsDevice: (id, body) =>
    request(`/api/fleet/gps-devices/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteGpsDevice: (id) =>
    request(`/api/fleet/gps-devices/${id}`, { method: "DELETE" }),
  // Retiro de un equipo del inventario: reason 'damaged' | 'lost'.
  retireGpsDevice: (id, reason) =>
    request(`/api/fleet/gps-devices/${id}/retire`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  reactivateGpsDevice: (id) =>
    request(`/api/fleet/gps-devices/${id}/reactivate`, { method: "POST" }),
  getSimCards: () => request("/api/fleet/sim-cards"),
  createSimCard: (body) =>
    request("/api/fleet/sim-cards", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateSimCard: (id, body) =>
    request(`/api/fleet/sim-cards/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteSimCard: (id) =>
    request(`/api/fleet/sim-cards/${id}`, { method: "DELETE" }),

  // Reassign ownership (master only) — resource: 'vehicles'|'drivers'|'gps-devices'|'sim-cards'
  reassignOwner: (resource, id, owner_id) =>
    request(`/api/fleet/${resource}/${id}/reassign`, {
      method: "PATCH",
      body: JSON.stringify({ owner_id }),
    }),

  // Agregado de la página de inicio de /admin: salud de la flota + contadores
  // de trabajo pendiente, todo en una sola petición.
  getAdminOverview: ({ signal } = {}) =>
    request("/api/admin/overview", { ...(signal ? { signal } : {}) }),

  // Pending devices
  getPendingDevices: () => request("/api/fleet/pending-devices"),
  approvePendingDevice: (id, body) =>
    request(`/api/fleet/pending-devices/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  rejectPendingDevice: (id, body) =>
    request(`/api/fleet/pending-devices/${id}/reject`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deletePendingDevice: (id) =>
    request(`/api/fleet/pending-devices/${id}`, { method: "DELETE" }),

  // Reporting profiles (frecuencia de reporte por dispositivo)
  getReportingProfiles: () => request("/api/reporting-profiles"),

  getReportingProfileImpact: (code, devices) =>
    request(
      `/api/reporting-profiles/impact?code=${encodeURIComponent(code)}&devices=${devices}`,
    ),

  getDeviceReportingProfile: (imei) =>
    request(`/api/devices/${seg(imei)}/reporting-profile`),

  setDeviceReportingProfile: (imei, profile) =>
    request(`/api/devices/${seg(imei)}/reporting-profile`, {
      method: "PUT",
      body: JSON.stringify({ profile }),
    }),

  applyReportingProfile: (profile, imeis) =>
    request("/api/reporting-profiles/apply", {
      method: "POST",
      body: JSON.stringify({ profile, imeis }),
    }),
  // Alert settings (per-device toggle)
  getAlertSettings: (imei) => request(`/api/devices/${seg(imei)}/alert-settings`),

  updateAlertSettings: (imei, settings) =>
    request(`/api/devices/${seg(imei)}/alert-settings`, {
      method: "PUT",
      body: JSON.stringify(settings),
    }),

  // Alert settings (by-type, for config panel)
  getAlertSettingsByType: (type) =>
    request(`/api/alert-settings/by-type/${type}`),

  bulkUpdateAlertSettings: (type, body) =>
    request(`/api/alert-settings/by-type/${type}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // Fleet (tenant-level) alert defaults — slice 4
  getFleetAlertDefaults: () => request("/api/alert-settings/fleet-defaults"),

  // Urgencia por tipo (configuración de flota). Decide si la alerta suena con
  // el teléfono bloqueado o entra callada a la bandeja.
  getAlertUrgency: () => request("/api/alert-settings/urgency"),

  updateAlertUrgency: (urgency) =>
    request("/api/alert-settings/urgency", {
      method: "PUT",
      body: JSON.stringify({ urgency }),
    }),

  // Alerts
  getAlerts: ({ plate, type, since, limit = 100, offset = 0 } = {}) => {
    const params = new URLSearchParams({ limit, offset });
    if (plate) params.set("plate", plate);
    if (type) params.set("type", type);
    if (since) params.set("since", since);
    return request(`/api/alerts?${params}`);
  },

  // Groups
  getGroups: () => request("/api/groups"),
  createGroup: (body) =>
    request("/api/groups", { method: "POST", body: JSON.stringify(body) }),
  updateGroup: (id, body) =>
    request(`/api/groups/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteGroup: (id) => request(`/api/groups/${id}`, { method: "DELETE" }),
  updateGroupMembers: (id, imeis) =>
    request(`/api/groups/${id}/members`, {
      method: "PUT",
      body: JSON.stringify({ imeis }),
    }),

  // Reports
  getRecorridos: (params) => {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, String(v)]),
    );
    return request(`/api/reports/recorridos?${qs}`);
  },
  getVelocidadReport: (params) => {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, String(v)]),
    );
    return request(`/api/reports/velocidad?${qs}`);
  },

  // ── Fotos (uploads staging + fotos por entidad) ──────────────────────────
  // El navegador fija Content-Type con el boundary multipart: nunca lo seteamos
  // a mano. multipart/form-data es un content-type "safelisted" por CORS → NO
  // dispara preflight, así que mandamos X-Requested-With (cabecera NO safelisted)
  // para forzar el preflight y que el backend lo exija como defensa anti-CSRF.
  // El Content-Type lo deja el navegador; sólo agregamos esa cabecera + Authorization
  // (via request()). Uploads son más pesados que el default de 10s → timeout de 30s.
  uploadPhoto: (blob, { width, height } = {}) => {
    const fd = new FormData();
    fd.append("photo", blob, "photo.webp");
    if (width) fd.append("width", String(width));
    if (height) fd.append("height", String(height));
    return request("/api/uploads", {
      method: "POST",
      body: fd,
      headers: { "X-Requested-With": "XMLHttpRequest" },
      timeoutMs: 30_000,
    });
  },
  getEntityPhotos: (entityType, id) =>
    request(`/api/${entityType}/${id}/photos`),
  addEntityPhoto: (entityType, id, blob, { width, height } = {}) => {
    const fd = new FormData();
    fd.append("photo", blob, "photo.webp");
    if (width) fd.append("width", String(width));
    if (height) fd.append("height", String(height));
    return request(`/api/${entityType}/${id}/photos`, {
      method: "POST",
      body: fd,
      headers: { "X-Requested-With": "XMLHttpRequest" },
      timeoutMs: 30_000,
    });
  },
  deleteEntityPhoto: (entityType, id, photoId) =>
    request(`/api/${entityType}/${id}/photos/${photoId}`, { method: "DELETE" }),

  // ── Billing ──────────────────────────────────────────────────────────────

  // Subscriptions
  getSubscriptions: ({ status, limit, offset } = {}) => {
    const params = new URLSearchParams(
      Object.entries({ status, limit, offset })
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => [k, String(v)]),
    );
    const qs = params.toString();
    return request(`/api/subscriptions${qs ? `?${qs}` : ""}`);
  },
  createSubscription: (payload) =>
    request("/api/subscriptions", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getTenantSubscription: (tenantId) =>
    request(`/api/subscriptions/tenant/${tenantId}`),
  updateTenantSubscription: (tenantId, patch) =>
    request(`/api/subscriptions/tenant/${tenantId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  updateTenantBillingProfile: (tenantId, patch) =>
    request(`/api/subscriptions/tenant/${tenantId}/billing-profile`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // Invoices
  getInvoices: ({
    tenant_id,
    status,
    period_start,
    period_end,
    limit,
    offset,
  } = {}) => {
    const params = new URLSearchParams(
      Object.entries({
        tenant_id,
        status,
        period_start,
        period_end,
        limit,
        offset,
      })
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => [k, String(v)]),
    );
    const qs = params.toString();
    return request(`/api/invoices${qs ? `?${qs}` : ""}`);
  },
  getInvoice: (invoiceId) => request(`/api/invoices/${invoiceId}`),
  generateInvoice: ({ tenant_id, period_year, period_month }) =>
    request("/api/invoices/generate", {
      method: "POST",
      body: JSON.stringify({ tenant_id, period_year, period_month }),
    }),
  voidInvoice: (invoiceId) =>
    request(`/api/invoices/${invoiceId}/void`, { method: "POST" }),
  markInvoiceWhatsappSent: (invoiceId) =>
    request(`/api/invoices/${invoiceId}/whatsapp-sent`, { method: "POST" }),
  getProratable: (tenantId) => {
    const qs = new URLSearchParams({ tenant_id: tenantId }).toString();
    return request(`/api/invoices/proratable?${qs}`);
  },
  createProratedInvoice: ({ tenant_id, device_ids, as_of_date }) =>
    request("/api/invoices/prorate", {
      method: "POST",
      body: JSON.stringify({ tenant_id, device_ids, as_of_date }),
    }),
  getInvoicePdfBlob: async (invoiceId) => {
    // Special case: needs response.blob(), not response.json().
    // Uses raw:true option so request() returns the raw Response object.
    const res = await request(`/api/invoices/${invoiceId}/pdf`, { raw: true });
    return res.blob();
  },

  // Payments
  // @deprecated usar createTenantPayment
  createInvoicePayment: (invoiceId, payload) =>
    request(`/api/invoices/${invoiceId}/payments`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  createTenantPayment: (tenantId, payload) =>
    request(`/api/tenants/${tenantId}/payments`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getInvoicePayments: (invoiceId) =>
    request(`/api/invoices/${invoiceId}/payments`),
  getPayments: ({ invoice_id, tenant_id } = {}) => {
    const params = new URLSearchParams(
      Object.entries({ invoice_id, tenant_id })
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => [k, String(v)]),
    );
    const qs = params.toString();
    return request(`/api/payments${qs ? `?${qs}` : ""}`);
  },
  deletePayment: (paymentId) =>
    request(`/api/payments/${paymentId}`, { method: "DELETE" }),

  // Billing status
  getTenantBillingStatus: (tenantId) =>
    request(`/api/tenants/${tenantId}/billing-status`),

  // Billing settings (singleton, migración 055) — defaults globales para
  // viewer-independientes creados por master.
  getBillingSettings: () => request("/api/billing/settings"),
  updateBillingSettings: (patch) =>
    request("/api/billing/settings", {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  // Device tenant transfer (master-only, migración 055).
  transferDevice: (imei, tenant_id) =>
    request(`/api/devices/${seg(imei)}/transfer`, {
      method: "POST",
      body: JSON.stringify({ tenant_id }),
    }),

  // ── Finanzas de Flota (contabilidad de vehículos en renta) ───────────────
  // Master opera sobre otro tenant pasando tenantId; owner/member lo omiten.

  finGetUnits: (tenantId) =>
    request(`/api/finanzas/units${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ""}`),
  finCreateUnit: (body) =>
    request("/api/finanzas/units", { method: "POST", body: JSON.stringify(body) }),
  // Hoy solo cambia la base por defecto de la unidad (migración 128).
  finUpdateUnit: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  finCreateAccount: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/accounts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Por dónde y hasta cuándo paga el conductor (migración 150). El cuerpo es el
  // bloque entero: `{ payment_deadline, accounts: [{ id, payment_kind,
  // payment_reference, payment_hours }] }`, y el orden del arreglo es el orden
  // en que se le nombran los medios.
  finUpdateUnitPaymentMethods: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/payment-methods`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  finGetPlanilla: (unitId) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/planilla`),
  finGetUnitContracts: (unitId) => request(`/api/finanzas/units/${encodeURIComponent(unitId)}/contracts`),
  finGetLedger: (unitId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    ).toString();
    return request(`/api/finanzas/units/${encodeURIComponent(unitId)}/ledger${qs ? `?${qs}` : ""}`);
  },
  finGetAvailableVehicles: (tenantId) =>
    request(
      `/api/finanzas/vehicles-available${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ""}`,
    ),
  finCreateContract: (body) =>
    request("/api/finanzas/contracts", { method: "POST", body: JSON.stringify(body) }),
  finUpdateContract: (contractId, body) =>
    request(`/api/finanzas/contracts/${encodeURIComponent(contractId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  // Tope de mantenimiento por moto al mes (migración 143). `null` quita el de la
  // unidad, y en el contrato vuelve a heredar el de su unidad.
  finUpdateUnitMaintenanceBudget: (unitId, maintenanceBudgetCop) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/maintenance/budget`, {
      method: "PUT",
      body: JSON.stringify({ maintenance_budget_cop: maintenanceBudgetCop }),
    }),
  finUpdateContractMaintenanceBudget: (contractId, maintenanceBudgetCop) =>
    request(`/api/finanzas/contracts/${encodeURIComponent(contractId)}/maintenance-budget`, {
      method: "PUT",
      body: JSON.stringify({ maintenance_budget_cop: maintenanceBudgetCop }),
    }),
  finPauseContract: (contractId, body) =>
    request(`/api/finanzas/contracts/${encodeURIComponent(contractId)}/pause`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finResumeContract: (contractId, body) =>
    request(`/api/finanzas/contracts/${encodeURIComponent(contractId)}/resume`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finCloseContract: (contractId, body) =>
    request(`/api/finanzas/contracts/${encodeURIComponent(contractId)}/close`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finRegisterPayment: (contractId, body) =>
    request(`/api/finanzas/contracts/${encodeURIComponent(contractId)}/payments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Abono a la BASE, no a la renta: completa la garantía que el conductor no
  // pudo dejar entera al llevarse la moto. No baja la deuda.
  finAddDepositTopUp: (contractId, body) =>
    request(`/api/finanzas/contracts/${encodeURIComponent(contractId)}/deposit-topup`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Corrección de un pago mal digitado (borra pago + allocations + asiento).
  finDeletePayment: (paymentId) =>
    request(`/api/finanzas/payments/${encodeURIComponent(paymentId)}`, { method: "DELETE" }),

  // La jornada entera en una transacción (todo o nada). `idempotency_key` es
  // obligatoria: si la red corta, reintentar con la MISMA clave devuelve los
  // pagos originales en vez de cobrar dos veces.
  finRegisterBulkPayments: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/bulk-payments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Lo anotado sin registrar: el bloqueo por mora lo descuenta antes de cortar.
  finSavePaymentDrafts: (unitId, drafts) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/payment-drafts`, {
      method: "PUT",
      body: JSON.stringify({ drafts }),
    }),
  // Retiros de capital del dueño. SOLO master: a un owner le responde 403.
  finGetCapitalWithdrawals: (unitId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    ).toString();
    return request(
      `/api/finanzas/units/${encodeURIComponent(unitId)}/capital-withdrawals${qs ? `?${qs}` : ""}`,
    );
  },

  // Movimientos manuales de caja (gastos e ingresos fuera de la renta).
  finCreateLedgerEntry: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/ledger`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finDeleteLedgerEntry: (entryId) =>
    request(`/api/finanzas/ledger/${encodeURIComponent(entryId)}`, { method: "DELETE" }),

  // Mantenimientos (el costo se asienta en el ledger como egreso).
  finGetMaintenance: (unitId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    ).toString();
    return request(
      `/api/finanzas/units/${encodeURIComponent(unitId)}/maintenance${qs ? `?${qs}` : ""}`,
    );
  },
  // Planilla por placa (la vista principal): una fila por vehículo de la
  // unidad, con su último servicio, su último cambio de aceite y el acumulado.
  finGetMaintenanceSheet: (unitId) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/maintenance/sheet`),
  // Sugerencias del alta: qué se escribe más y a qué precio en esta unidad.
  finGetMaintenanceSuggestions: (unitId) =>
    request(
      `/api/finanzas/units/${encodeURIComponent(unitId)}/maintenance/suggestions`,
    ),
  // Cada cuánto toca el próximo servicio en esta unidad (días o km).
  finGetMaintenancePolicy: (unitId) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/maintenance/policy`),
  finUpdateMaintenancePolicy: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/maintenance/policy`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  // Política de bloqueo por mora de la unidad (horario, umbral, corte, prórroga).
  getLockPolicy: (unitId) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/lock-policy`),
  updateLockPolicy: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/lock-policy`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  // Horario de funcionamiento: preset de la unidad y horario de cada vehículo.
  getOperatingSchedule: (unitId) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/operating-schedule`),
  updateOperatingSchedule: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/operating-schedule`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  updateVehicleSchedules: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/operating-schedule/vehicles`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  // Kilometraje actual del vehículo (lectura del tablero + recorrido del GPS).
  finGetVehicleOdometer: (vehicleId) =>
    request(`/api/finanzas/vehicles/${encodeURIComponent(vehicleId)}/odometer`),
  finGetMaintenanceReminders: (unitId) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/maintenance/reminders`),
  finCreateMaintenance: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/maintenance`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finDeleteMaintenance: (maintenanceId) =>
    request(`/api/finanzas/maintenance/${encodeURIComponent(maintenanceId)}`, { method: "DELETE" }),
  finGetVehicleInspections: (vehicleId) =>
    request(`/api/finanzas/vehicles/${encodeURIComponent(vehicleId)}/inspections`),

  // Obligaciones: préstamos (por pagar / por cobrar) y ahorros en cuotas.
  // El desembolso y cada cuota se asientan en el ledger de la unidad.
  finGetObligations: (unitId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    ).toString();
    return request(
      `/api/finanzas/units/${encodeURIComponent(unitId)}/obligations${qs ? `?${qs}` : ""}`,
    );
  },
  finGetObligationsUpcoming: (unitId) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/obligations/upcoming`),
  finCreateObligation: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/obligations`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finGetObligation: (obligationId) =>
    request(`/api/finanzas/obligations/${encodeURIComponent(obligationId)}`),
  finDeleteObligation: (obligationId) =>
    request(`/api/finanzas/obligations/${encodeURIComponent(obligationId)}`, { method: "DELETE" }),
  finRegisterObligationPayment: (obligationId, body) =>
    request(`/api/finanzas/obligations/${encodeURIComponent(obligationId)}/payments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finDeleteObligationPayment: (paymentId) =>
    request(`/api/finanzas/obligation-payments/${encodeURIComponent(paymentId)}`, {
      method: "DELETE",
    }),

  // Historial de renta + puntaje de cumplimiento de un conductor. Requiere la
  // feature `finanzas`: sin ella responde 402/403 y el perfil del conductor
  // muestra la ficha sin la sección de pagos (no es un error a reportar).
  finGetDriverPaymentProfile: (driverId) =>
    request(`/api/finanzas/drivers/${encodeURIComponent(driverId)}/payment-profile`),
  // Arqueo: saldo teórico por caja y conteos físicos (el descuadre se asienta).
  finGetCashBalances: (unitId, date) =>
    request(
      `/api/finanzas/units/${encodeURIComponent(unitId)}/cash-balances${date ? `?date=${encodeURIComponent(date)}` : ""}`,
    ),
  finGetCashCounts: (unitId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    ).toString();
    return request(
      `/api/finanzas/units/${encodeURIComponent(unitId)}/cash-counts${qs ? `?${qs}` : ""}`,
    );
  },
  finRegisterCashCount: (unitId, body) =>
    request(`/api/finanzas/units/${encodeURIComponent(unitId)}/cash-counts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finDeleteCashCount: (unitId, cashCountId) =>
    request(
      `/api/finanzas/units/${encodeURIComponent(unitId)}/cash-counts/${encodeURIComponent(cashCountId)}`,
      { method: "DELETE" },
    ),

  // Dashboard: KPIs de todas las unidades del tenant + consolidado.
  finGetDashboard: (tenantId, period) => {
    const qs = new URLSearchParams({ period });
    if (tenantId) qs.set("tenant_id", tenantId);
    return request(`/api/finanzas/dashboard?${qs.toString()}`);
  },

  // Cierre del periodo: P&L por concepto y por placa ('YYYY-MM' o 'YYYY').
  finGetClosing: (unitId, period) =>
    request(
      `/api/finanzas/units/${encodeURIComponent(unitId)}/closing?period=${encodeURIComponent(period)}`,
    ),

  // Catálogo de conceptos (rubros del cierre mensual).
  finGetConcepts: (tenantId, { includeInactive = false } = {}) => {
    const qs = new URLSearchParams();
    if (tenantId) qs.set("tenant_id", tenantId);
    if (includeInactive) qs.set("include_inactive", "true");
    const suffix = qs.toString();
    return request(`/api/finanzas/concepts${suffix ? `?${suffix}` : ""}`);
  },
  finCreateConcept: (body) =>
    request("/api/finanzas/concepts", { method: "POST", body: JSON.stringify(body) }),
  finUpdateConcept: (conceptId, body) =>
    request(`/api/finanzas/concepts/${encodeURIComponent(conceptId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  finSeedDefaultConcepts: (tenantId) =>
    request(
      `/api/finanzas/concepts/defaults${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ""}`,
      { method: "POST" },
    ),

  // ── Admin Catalog (plans, features) ──────────────────────────────────────

  // Plans
  getPlans: () => request("/api/admin/plans"),
  createPlan: (body) =>
    request("/api/admin/plans", { method: "POST", body: JSON.stringify(body) }),
  updatePlan: (id, patch) =>
    request(`/api/admin/plans/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  deletePlan: (id) => request(`/api/admin/plans/${id}`, { method: "DELETE" }),
  updatePlanFeatures: (planId, featureKeys) =>
    request(`/api/admin/plans/${planId}/features`, {
      method: "PUT",
      body: JSON.stringify({ feature_keys: featureKeys }),
    }),

  // Features
  getFeatures: () => request("/api/admin/features"),
  createFeature: (body) =>
    request("/api/admin/features", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateFeature: (key, patch) =>
    request(`/api/admin/features/${encodeURIComponent(key)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  deleteFeature: (key) =>
    request(`/api/admin/features/${encodeURIComponent(key)}`, {
      method: "DELETE",
    }),

  // Tenant plan assignment
  updateTenantPlan: (tenantId, planId) =>
    request(`/api/admin/tenants/${tenantId}/plan`, {
      method: "PATCH",
      body: JSON.stringify({ plan_id: planId }),
    }),

  // ── Geofences ────────────────────────────────────────────────────────────

  // Geofences
  getGeofences: () => request("/api/geofences"),
  getGeofencePresets: () => request("/api/geofences/presets"),
  importGeofencePreset: (presetId, { imeis = [] } = {}) =>
    request(`/api/geofences/presets/${presetId}/import`, {
      method: "POST",
      body: JSON.stringify({ imeis }),
    }),
  createGeofence: (body) =>
    request("/api/geofences", { method: "POST", body: JSON.stringify(body) }),
  getGeofence: (id) => request(`/api/geofences/${id}`),
  updateGeofence: (id, body) =>
    request(`/api/geofences/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteGeofence: (id) => request(`/api/geofences/${id}`, { method: "DELETE" }),
  getGeofenceEvents: (id, limit = 50) =>
    request(`/api/geofences/${id}/events?limit=${limit}`),
  updateGeofenceAssignments: (id, imeis) =>
    request(`/api/geofences/${id}/assignments`, {
      method: "PUT",
      body: JSON.stringify({ imeis }),
    }),

  // ── Dashboard operativo ──────────────────────────────────────────────────

  getDashboardGeofencePresence: (geofenceId, { signal } = {}) =>
    request(
      `/api/dashboard/geofence-presence?geofenceId=${encodeURIComponent(geofenceId)}`,
      { signal },
    ),
  getDashboardNightActivity: ({ start, end, minSpeed } = {}, { signal } = {}) => {
    const params = new URLSearchParams({ start, end });
    if (minSpeed != null) params.set("minSpeed", minSpeed);
    return request(`/api/dashboard/night-activity?${params.toString()}`, {
      signal,
    });
  },

  // Notifications — Web Push
  getVapidPublicKey: () => request("/api/push/vapid-public-key"),
  getPushSubscriptions: () => request("/api/push/subscriptions"),
  subscribePush: (body) =>
    request("/api/push/subscribe", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  unsubscribePush: (body) =>
    request("/api/push/subscribe", {
      method: "DELETE",
      body: JSON.stringify(body),
    }),

  // Notifications — push nativo (tokens de dispositivo de Capacitor)
  registerDeviceToken: (body) =>
    request("/api/push/device-token", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  unregisterDeviceToken: (token) =>
    request("/api/push/device-token", {
      method: "DELETE",
      body: JSON.stringify({ token }),
    }),
  getDeviceTokens: () => request("/api/push/device-tokens"),
  unlinkDeviceToken: (id) =>
    request(`/api/push/device-tokens/${id}`, { method: "DELETE" }),

  // Notifications — Telegram pairing
  getTelegramStatus: () => request("/api/telegram/status"),
  pairTelegram: () => request("/api/telegram/pair", { method: "POST" }),
  unlinkTelegram: () => request("/api/telegram/link", { method: "DELETE" }),
  unlinkTelegramChat: (chatId) =>
    request(`/api/telegram/chats/${chatId}`, { method: "DELETE" }),

  // Notifications — per-user channel preferences
  getNotificationPreferences: () => request("/api/notification-preferences"),
  updateNotificationPreferences: (preferences) =>
    request("/api/notification-preferences", {
      method: "PUT",
      body: JSON.stringify({ preferences }),
    }),
};
